import { LocateFixedIcon, WebhookIcon } from 'lucide-react';

import useMembers from '@/hooks/useMembers';

import { ResourceTile } from '../../components/ResourceList/ResourceTile';
import {
  ActiveStatus,
  CreatorMeta,
  ResourceActiveSwitch,
  ResourceTileMeta,
} from '../../components/ResourceList/ResourceTileParts';
import { EventOption } from '../../hooks/useEventOptions';
import { Plugin } from '../../types';

interface PluginItemProps {
  plugin: Plugin;
  eventOptions: EventOption[];
  isSelected: boolean;
  canWrite: boolean;
  onEdit: (plugin: Plugin) => void;
  onToggleActive: (plugin: Plugin) => void;
}

export const PluginItem = ({
  plugin,
  eventOptions,
  isSelected,
  canWrite,
  onEdit,
  onToggleActive,
}: PluginItemProps) => {
  const { getMember } = useMembers();
  const creator = plugin.created_by ? getMember(plugin.created_by) : undefined;
  const eventLabels = plugin.events
    .map((event) => eventOptions.find((option) => option.value === event)?.label ?? event)
    .join(', ');

  return (
    <ResourceTile
      icon={<WebhookIcon aria-hidden="true" className="h-6 w-6 text-muted-foreground" />}
      title={plugin.name}
      status={<ActiveStatus active={plugin.active} />}
      meta={
        <ResourceTileMeta>
          {creator && <CreatorMeta member={creator} />}
          {eventLabels ? (
            <span className="flex min-w-0 items-center gap-1">
              <LocateFixedIcon aria-hidden="true" size={16} className="shrink-0" />
              <span className="min-w-0 truncate">{eventLabels}</span>
            </span>
          ) : null}
        </ResourceTileMeta>
      }
      toggle={
        <ResourceActiveSwitch
          name={plugin.name}
          checked={plugin.active}
          onCheckedChange={() => onToggleActive(plugin)}
          disabled={!canWrite}
        />
      }
      onOpen={() => onEdit(plugin)}
      isSelected={isSelected}
    />
  );
};
